import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CampaignRanking } from '../../types/campaignRanking';
import { CampaignScorecardModal } from './CampaignScorecardModal';
import { Input } from '@/components/ui/input';

interface TopPerformingCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  rankings: CampaignRanking[];
}

export const TopPerformingCampaignsModal: React.FC<TopPerformingCampaignsModalProps> = ({ isOpen, onClose, rankings }) => {
  const [search, setSearch] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignRanking | null>(null);

  const filteredRankings = rankings.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));

  const sortedRankings = [...filteredRankings].sort((a, b) => b.overallScore - a.overallScore);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Hanken_Grotesk']">
              Campaign Performance Rankings
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col md:flex-row justify-between gap-4 py-4">
            <Input 
              placeholder="Search campaigns..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm text-left text-slate-600">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 w-16">Rank</th>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Views</th>
                  <th className="px-4 py-3">ER</th>
                  <th className="px-4 py-3">Overall Score</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedRankings.map((campaign, index) => (
                  <tr key={campaign.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-slate-500 font-medium">#{index + 1}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{campaign.name}</td>
                    <td className="px-4 py-3">{campaign.metrics.totalViews.toLocaleString()}</td>
                    <td className="px-4 py-3">{campaign.performance.engagementRate.toFixed(1)}%</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        campaign.tier === 'High' ? 'bg-emerald-50 text-emerald-600' :
                        campaign.tier === 'Medium' ? 'bg-amber-50 text-amber-600' :
                        'bg-rose-50 text-rose-600'
                      }`}>
                        {campaign.overallScore}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button 
                        onClick={() => setSelectedCampaign(campaign)}
                        className="text-[#A655F8] hover:text-[#8455EF] font-semibold text-xs transition-colors"
                      >
                        Scorecard
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sortedRankings.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                No campaigns found matching your criteria.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {selectedCampaign && (
        <CampaignScorecardModal 
          isOpen={!!selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          campaign={selectedCampaign}
        />
      )}
    </>
  );
};
